import React from 'react';
import {
  Link,
  Toast,
  ToastButton,
  createToast,
  dismissToast,
} from 'components/core';

// A stable id so a repeated failure replaces the existing toast rather than
// stacking, and so we can dismiss it when the reviewer retries or steps away.
const ERROR_TOAST_ID = 'libkey-nomad-migration-error';

// The LibKey settings page, where the reviewer can pick their library by hand.
// The auto-switch can fail for good (e.g. the harvested Nomad library isn't in
// Covidence's list), so retrying gets them nowhere — this is the way out.
export const PROFILE_LIBKEY_PATH = '/profile/integrations';

// Cross-reload signal. The switch persists the library then reloads so the
// native LibKey buttons render (and Nomad's `ref-ids` hook stops being emitted);
// this flag carries the "show the success toast" intent across that reload.
// sessionStorage (not local) scopes it to the tab and clears it when the tab
// closes, so a stale flag can never resurface the toast later.
const PENDING_SUCCESS_KEY = 'covidence.libkey.migrationSucceeded';

export const showMigrationErrorToast = (
  onRetry: (event: React.MouseEvent<HTMLButtonElement>) => void,
  restoreFocusTo?: HTMLElement | null
) => {
  createToast(
    <Toast
      type="danger"
      role="alert"
      focusOnAppear
      restoreFocusTo={restoreFocusTo}
    >
      Something went wrong, please try again
      <ToastButton
        htmlType="button"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          dismissToast(ERROR_TOAST_ID);
          onRetry(event);
        }}
      >
        Try again
      </ToastButton>
    </Toast>,
    { id: ERROR_TOAST_ID }
  );
};

// Shown after a retry has also failed: a "Try again" loop won't recover a
// permanently-unmappable library, so point the reviewer at the settings page to
// choose one themselves. Reuses ERROR_TOAST_ID so it replaces the retry toast.
export const showMigrationHelpToast = (restoreFocusTo?: HTMLElement | null) => {
  createToast(
    <Toast
      type="danger"
      role="alert"
      focusOnAppear
      restoreFocusTo={restoreFocusTo}
    >
      We couldn&apos;t switch you over automatically.
      <br />
      Choose your library on the profile page
      <Link to={PROFILE_LIBKEY_PATH}>View profile</Link>
    </Toast>,
    { id: ERROR_TOAST_ID }
  );
};

export const dismissMigrationErrorToast = () => dismissToast(ERROR_TOAST_ID);

export const showMigrationSuccessToast = () => {
  createToast(
    <Toast type="success">Successfully enabled LibKey integration</Toast>
  );
};

export const markMigrationSucceeded = () => {
  try {
    window.sessionStorage.setItem(PENDING_SUCCESS_KEY, 'true');
  } catch {
    // Non-fatal: the reviewer simply won't see the confirmation toast.
  }
};

// Reads and clears the pending-success flag in one step, so the toast shows
// exactly once after the reload.
export const consumeMigrationSucceeded = (): boolean => {
  try {
    if (window.sessionStorage.getItem(PENDING_SUCCESS_KEY) === 'true') {
      window.sessionStorage.removeItem(PENDING_SUCCESS_KEY);
      return true;
    }
  } catch {
    // Treat storage failures as "nothing pending".
  }
  return false;
};
